import clsx from 'clsx';
import {
  ComponentProps,
  PropsWithChildren,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { FormProvider, useFormContext } from 'react-hook-form';
import { Modal as RSModal } from 'reactstrap';
import {
  darkModeLightActive,
  darkModeLightActiveOutline,
} from 'src/components/Reports/Reports.css';
import { Content, useContent } from 'src/contexts/ContentContext';
import { FilterQueryContextProvider } from 'src/contexts/FilterQueryContext';
import { useSiteTheme } from 'src/contexts/SiteTheme/SiteThemeContext';
import { GenericDialog } from 'src/core/interim/dialogs/GenericDialog';
import { PosFormField } from 'src/core/POS/PosFormField';
import { getTextFieldState, PosTextField } from 'src/core/POS/PosTextField';
import { shared, vars } from 'src/core/themes';
import { Colors } from 'src/core/themes/constants/colors';
import { Button, Radio, RadioGroup, Stack } from 'src/core/ui';
import { useReportConfigsV2 } from 'src/hooks/reportConfigs';
import { ReportConfigV2 } from 'src/hooks/reportConfigs';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { FieldWrapper } from 'src/modals/common/Purchase';
import { ContentId } from 'src/utils/constants/contentId';
import {
  EmptyReportV2CostOfGoodsSoldQuery,
  EmptyReportV2ListingQuery,
  EmptyReportV2PurchaseOrderLineItemQuery,
  EmptyReportV2PurchaseQuery,
  EmptyReportV2SaleCostAdjustmentsQuery,
  EmptyReportV2SaleLineItemQuery,
  EmptyReportV2SaleQuery,
  EmptyReportV2SoldTicketQuery,
  EmptyReportV2TicketGroupQuery,
} from 'src/utils/eventQueryUtils';
import { ReportTypesV2 } from 'src/utils/reportsUtils';
import {
  Feature,
  UIReportV2CostOfGoodsSoldQuery,
  UIReportV2ListingQuery,
  UIReportV2PurchaseOrderLineItemQuery,
  UIReportV2PurchaseQuery,
  UIReportV2SaleLineItemQuery,
  UIReportV2SaleQuery,
  UIReportV2SaleTotalCostAdjustmentsQuery,
  UIReportV2SoldTicketQuery,
  UIReportV2TicketGroupQuery,
} from 'src/WebApiController';

import { REPORT_TYPE_TO_EMPTY_FILTER_QUERY } from './ReportBuiderDialog.types';
import * as styles from './ReportBuilderDialog.css';
import { ReportBuilderFilterInputV2 } from './ReportBuilderFilterInputV2';

export type ReportBuilderDialogProps = {
  report: ReportConfigV2;
  onSave: (data: ReportConfigV2) => void;
} & ComponentProps<typeof RSModal>;

export function ReportBuilderDialogBodyV2({
  onSave,
  onClosed,
  register,
  setError,
  setValue,
  handleSubmit,
  formState,
  watch,
  clearErrors,
  resetField,
  ...rest
}: Omit<ReportBuilderDialogProps, 'report'> &
  Omit<
    ComponentProps<typeof FormProvider<ReportConfigV2, unknown>>,
    'children' | 'trigger' | 'reset' | 'unregister'
  >) {
  const { isDarkMode } = useSiteTheme();
  const { reportConfigs: existingReports } = useReportConfigsV2({});

  const requiredMsg = useContent(ContentId.Required);
  const reportNameText = useContent(ContentId.ReportName);
  const duplicateNameMsg = useContent(ContentId.DuplicatedReportName);

  const hasPoLineItemReportFeature = useUserHasFeature(
    Feature.PurchaseLineItemReport
  );
  const hasCostChangeReportsFeature = useUserHasFeature(
    Feature.CostChangeReports
  );
  const hasTicketGroupReportFeature = useUserHasFeature(
    Feature.TicketGroupReport
  );

  const reportNameFieldRef = useRef<HTMLInputElement>(null);

  const nameError = formState.errors.reportName?.message;
  const filtersError = formState.errors.request?.filters?.message;

  const reportId = watch('reportId');
  const reportName = watch('reportName');
  const reportType = watch('reportType');

  const existingReportNames = useMemo(() => {
    return existingReports
      ?.filter((r) => r.reportId !== reportId)
      .map((r) => r.reportName);
  }, [existingReports, reportId]);

  const onSubmit = useCallback(() => {
    let hasErrors = false;
    if (!reportName) {
      setError('reportName', { message: requiredMsg }, { shouldFocus: true });
      hasErrors = true;
    }

    if (
      existingReportNames.some(
        (name) => name.toLocaleUpperCase() === reportName.toLocaleUpperCase()
      )
    ) {
      setError(
        'reportName',
        { message: duplicateNameMsg },
        { shouldFocus: true }
      );
      hasErrors = true;
    }

    if (!hasErrors) {
      handleSubmit(onSave)();
    }
  }, [
    reportName,
    existingReportNames,
    setError,
    requiredMsg,
    duplicateNameMsg,
    handleSubmit,
    onSave,
  ]);

  return (
    <>
      <GenericDialog
        {...rest}
        size="md"
        header={
          <Content
            id={
              reportId == null
                ? ContentId.NewCustomReport
                : ContentId.EditReport
            }
          />
        }
        footer={
          <Button
            style={{
              width: '100%',
              margin: `${vars.spacing.m} ${vars.spacing.lg}`,
              backgroundColor: vars.color.textBrand,
              color: isDarkMode ? Colors.dark060 : undefined,
            }}
            variant={'regular'}
            onClick={onSubmit}
            disabled={formState.isSubmitting}
          >
            {reportId == null ? (
              <Content id={ContentId.BuildReport} />
            ) : (
              <Content id={ContentId.Save} />
            )}
          </Button>
        }
        onCancel={onClosed}
        onOpened={() => {
          reportNameFieldRef.current?.focus();
        }}
      >
        <div className={styles.reportBuilderDialogContainer}>
          <FieldWrapper>
            <PosFormField errors={nameError} label={<></>}>
              <PosTextField
                rootProps={{
                  disabled: formState.isSubmitting,
                  state: getTextFieldState(nameError),
                }}
                disabled={formState.isSubmitting}
                value={reportName}
                onChange={(e) => {
                  setValue('reportName', e.target.value);
                  clearErrors('reportName');
                }}
                ref={reportNameFieldRef}
                spellCheck={false}
                trimOnBlur={true}
                placeholder={reportNameText}
              />
            </PosFormField>
          </FieldWrapper>

          <FieldWrapper>
            <PosFormField label={<></>}>
              <RadioGroup
                disabled={
                  formState.isSubmitting ||
                  // We don't support changing the report type of an existing report yet
                  reportId != null
                }
                style={{ width: '100%' }}
                onValueChange={(value) => {
                  const newValue = value as ReportTypesV2;
                  if (newValue === reportType) {
                    // Nothing to do
                    return;
                  }

                  if (formState.defaultValues?.reportType === newValue) {
                    const defaultValues =
                      formState.defaultValues as ReportConfigV2;
                    const defaultRequest = defaultValues.request;
                    // Restore filters and metrics
                    setValue(
                      'request.aggregations',
                      defaultRequest.aggregations
                    );
                    setValue(
                      'request.columnGroupings',
                      defaultRequest.columnGroupings
                    );
                    setValue(
                      'request.rowGroupings',
                      defaultRequest.rowGroupings
                    );
                    setValue('request.filters', defaultRequest.filters);
                    setValue(
                      'hiddenFilterItemIds',
                      defaultValues.hiddenFilterItemIds
                    );
                    setValue(
                      'viewableFilterItemIds',
                      defaultValues.viewableFilterItemIds
                    );
                    setValue(
                      'editableFilterItemIds',
                      defaultValues.editableFilterItemIds
                    );
                  } else {
                    // Clear filters and metrics
                    setValue('request.aggregations', []);
                    setValue('request.columnGroupings', []);
                    setValue('request.rowGroupings', []);
                    setValue('hiddenFilterItemIds', []);
                    setValue('viewableFilterItemIds', []);
                    setValue('editableFilterItemIds', []);

                    setValue(
                      'request.filters',
                      REPORT_TYPE_TO_EMPTY_FILTER_QUERY[newValue]
                    );
                  }
                  setValue('reportType', newValue);
                }}
                value={reportType}
              >
                <Stack direction="column" gap="m">
                  <Radio
                    value={ReportTypesV2.InventoryV2}
                    checked={reportType === ReportTypesV2.InventoryV2}
                    containerClassName={styles.reportTypeLabel}
                    disabled={
                      formState.isSubmitting ||
                      // We don't support changing the report type of an existing report yet
                      reportId != null
                    }
                    label={
                      <Stack direction="column">
                        <span>
                          <Content id={ContentId.Listings} />
                        </span>
                        <span className={styles.reportTypeSubLabel}>
                          <Content id={ContentId.InventoryReportDescription} />
                        </span>
                      </Stack>
                    }
                    variant="outline"
                  />
                  <Radio
                    value={ReportTypesV2.SaleV2}
                    checked={reportType === ReportTypesV2.SaleV2}
                    containerClassName={styles.reportTypeLabel}
                    disabled={
                      formState.isSubmitting ||
                      // We don't support changing the report type of an existing report yet
                      reportId != null
                    }
                    label={
                      <Stack direction="column">
                        <span>
                          <Content id={ContentId.Sales} />
                        </span>
                        <span className={styles.reportTypeSubLabel}>
                          <Content id={ContentId.SaleReportDescription} />
                        </span>
                      </Stack>
                    }
                    variant="outline"
                  />
                  <Radio
                    value={ReportTypesV2.Purchase}
                    checked={reportType === ReportTypesV2.Purchase}
                    containerClassName={styles.reportTypeLabel}
                    disabled={
                      formState.isSubmitting ||
                      // We don't support changing the report type of an existing report yet
                      reportId != null
                    }
                    label={
                      <Stack direction="column">
                        <span>
                          <Content id={ContentId.Purchases} />
                        </span>
                        <span className={styles.reportTypeSubLabel}>
                          <Content id={ContentId.PurchaseReportDescription} />
                        </span>
                      </Stack>
                    }
                    variant="outline"
                  />
                  {hasTicketGroupReportFeature && (
                    <Radio
                      value={ReportTypesV2.TicketGroup}
                      checked={reportType === ReportTypesV2.TicketGroup}
                      containerClassName={styles.reportTypeLabel}
                      disabled={
                        formState.isSubmitting ||
                        // We don't support changing the report type of an existing report yet
                        reportId != null
                      }
                      label={
                        <Stack direction="column">
                          <span>
                            <Content id={ContentId.PurchaseLines} />
                          </span>
                          <span className={styles.reportTypeSubLabel}>
                            <Content
                              id={ContentId.TicketGroupReportDescription}
                            />
                          </span>
                        </Stack>
                      }
                      variant="outline"
                    />
                  )}
                  <Radio
                    value={ReportTypesV2.SaleLineItem}
                    checked={reportType === ReportTypesV2.SaleLineItem}
                    containerClassName={styles.reportTypeLabel}
                    disabled={
                      formState.isSubmitting ||
                      // We don't support changing the report type of an existing report yet
                      reportId != null
                    }
                    label={
                      <Stack direction="column">
                        <span>
                          <Content id={ContentId.SaleLineItems} />
                        </span>
                        <span className={styles.reportTypeSubLabel}>
                          <Content
                            id={ContentId.SaleLineItemReportDescription}
                          />
                        </span>
                      </Stack>
                    }
                    variant="outline"
                  />
                  {hasPoLineItemReportFeature && (
                    <Radio
                      value={ReportTypesV2.PurchaseOrderLineItem}
                      checked={
                        reportType === ReportTypesV2.PurchaseOrderLineItem
                      }
                      containerClassName={styles.reportTypeLabel}
                      disabled={
                        formState.isSubmitting ||
                        // We don't support changing the report type of an existing report yet
                        reportId != null
                      }
                      label={
                        <Stack direction="column">
                          <span>
                            <Content id={ContentId.PurchaseLineItems} />
                          </span>
                          <span className={styles.reportTypeSubLabel}>
                            <Content
                              id={ContentId.PurchaseLineItemReportDescription}
                            />
                          </span>
                        </Stack>
                      }
                      variant="outline"
                    />
                  )}
                  <Radio
                    value={ReportTypesV2.SoldTicket}
                    checked={reportType === ReportTypesV2.SoldTicket}
                    containerClassName={styles.reportTypeLabel}
                    disabled={
                      formState.isSubmitting ||
                      // We don't support changing the report type of an existing report yet
                      reportId != null
                    }
                    label={
                      <Stack direction="column">
                        <span>
                          <Content id={ContentId.SoldTickets} />
                        </span>
                        <span className={styles.reportTypeSubLabel}>
                          <Content id={ContentId.SoldTicketReportDescription} />
                        </span>
                      </Stack>
                    }
                    variant="outline"
                  />
                  {hasCostChangeReportsFeature && (
                    <>
                      <Radio
                        value={ReportTypesV2.CostOfGoodsSold}
                        checked={reportType === ReportTypesV2.CostOfGoodsSold}
                        containerClassName={styles.reportTypeLabel}
                        disabled={
                          formState.isSubmitting ||
                          // We don't support changing the report type of an existing report yet
                          reportId != null
                        }
                        label={
                          <Stack direction="column">
                            <span>
                              <Content
                                id={ContentId.PurchaseTicketAdjustment}
                              />
                            </span>
                            <span className={styles.reportTypeSubLabel}>
                              <Content
                                id={
                                  ContentId.PurchaseTicketAdjustmentReportDescription
                                }
                              />
                            </span>
                          </Stack>
                        }
                        variant="outline"
                      />
                      <Radio
                        value={ReportTypesV2.SaleCostAdjustments}
                        checked={
                          reportType === ReportTypesV2.SaleCostAdjustments
                        }
                        containerClassName={styles.reportTypeLabel}
                        disabled={
                          formState.isSubmitting ||
                          // We don't support changing the report type of an existing report yet
                          reportId != null
                        }
                        label={
                          <Stack direction="column">
                            <span>
                              <Content id={ContentId.SaleCostAdjustments} />
                            </span>
                            <span className={styles.reportTypeSubLabel}>
                              <Content
                                id={
                                  ContentId.SaleCostAdjustmentsReportDescription
                                }
                              />
                            </span>
                          </Stack>
                        }
                        variant="outline"
                      />
                    </>
                  )}
                </Stack>
              </RadioGroup>
            </PosFormField>
          </FieldWrapper>

          <Stack direction="column">
            <PosFormField errors={filtersError} label={<></>} />
            <div>
              <FilterInput disabled={formState.isSubmitting} />
            </div>
          </Stack>
        </div>
      </GenericDialog>
    </>
  );
}

const FilterInput = ({ disabled }: { disabled?: boolean }) => {
  const { watch } = useFormContext<ReportConfigV2>();
  const reportType = watch('reportType');

  return (
    <FilterInputContextWrapper>
      <ReportBuilderFilterInputV2
        reportType={reportType}
        direction="column"
        filtersListHeader={
          <span className={shared.typography.body1}>
            <Content id={ContentId.FilterReportOptional} />
          </span>
        }
        addFilterDropdownProps={{
          triggerShape: 'pill',
          triggerVariant: 'outline',
          triggerClassName: clsx(
            styles.addFilterButton,
            darkModeLightActive,
            darkModeLightActiveOutline
          ),
        }}
        disabled={disabled}
      />
    </FilterInputContextWrapper>
  );
};

const FilterInputContextWrapper = ({ children }: PropsWithChildren<object>) => {
  const { watch } = useFormContext<ReportConfigV2>();
  const reportType = watch('reportType');
  const reportFilter = watch('request.filters');

  if (reportType === ReportTypesV2.InventoryV2) {
    return (
      <FilterQueryContextProvider<UIReportV2ListingQuery>
        initialQuery={reportFilter as UIReportV2ListingQuery}
        emptyQuery={EmptyReportV2ListingQuery}
      >
        {children}
      </FilterQueryContextProvider>
    );
  }

  if (reportType === ReportTypesV2.SaleLineItem) {
    return (
      <FilterQueryContextProvider<UIReportV2SaleLineItemQuery>
        initialQuery={reportFilter as UIReportV2SaleLineItemQuery}
        emptyQuery={EmptyReportV2SaleLineItemQuery}
      >
        {children}
      </FilterQueryContextProvider>
    );
  }

  if (reportType === ReportTypesV2.SoldTicket) {
    return (
      <FilterQueryContextProvider<UIReportV2SoldTicketQuery>
        initialQuery={reportFilter as UIReportV2SoldTicketQuery}
        emptyQuery={EmptyReportV2SoldTicketQuery}
      >
        {children}
      </FilterQueryContextProvider>
    );
  }

  if (reportType === ReportTypesV2.Purchase) {
    return (
      <FilterQueryContextProvider<UIReportV2PurchaseQuery>
        initialQuery={reportFilter as UIReportV2PurchaseQuery}
        emptyQuery={EmptyReportV2PurchaseQuery}
      >
        {children}
      </FilterQueryContextProvider>
    );
  }

  if (reportType === ReportTypesV2.PurchaseOrderLineItem) {
    return (
      <FilterQueryContextProvider<UIReportV2PurchaseOrderLineItemQuery>
        initialQuery={reportFilter as UIReportV2PurchaseOrderLineItemQuery}
        emptyQuery={EmptyReportV2PurchaseOrderLineItemQuery}
      >
        {children}
      </FilterQueryContextProvider>
    );
  }

  if (reportType === ReportTypesV2.CostOfGoodsSold) {
    return (
      <FilterQueryContextProvider<UIReportV2CostOfGoodsSoldQuery>
        initialQuery={reportFilter as UIReportV2CostOfGoodsSoldQuery}
        emptyQuery={EmptyReportV2CostOfGoodsSoldQuery}
      >
        {children}
      </FilterQueryContextProvider>
    );
  }

  if (reportType === ReportTypesV2.SaleCostAdjustments) {
    return (
      <FilterQueryContextProvider<UIReportV2SaleTotalCostAdjustmentsQuery>
        initialQuery={reportFilter as UIReportV2SaleTotalCostAdjustmentsQuery}
        emptyQuery={EmptyReportV2SaleCostAdjustmentsQuery}
      >
        {children}
      </FilterQueryContextProvider>
    );
  }

  if (reportType === ReportTypesV2.TicketGroup) {
    return (
      <FilterQueryContextProvider<UIReportV2TicketGroupQuery>
        initialQuery={reportFilter as UIReportV2TicketGroupQuery}
        emptyQuery={EmptyReportV2TicketGroupQuery}
      >
        {children}
      </FilterQueryContextProvider>
    );
  }

  return (
    <FilterQueryContextProvider<UIReportV2SaleQuery>
      initialQuery={reportFilter as UIReportV2SaleQuery}
      emptyQuery={EmptyReportV2SaleQuery}
    >
      {children}
    </FilterQueryContextProvider>
  );
};
